/**
 * Lending configuration hooks (GH #4688)
 *
 * React Query wrappers over lendingConfigApi for the caller's own
 * institution. A refused read for a missing (404) or incomplete (503)
 * configuration is a state of the institution, so it is not retried. The
 * defaults are fetched only when an editor asks for them. A save writes the
 * stored configuration lending-platform answers into the query cache.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isLendingConfigurationIncomplete,
  isLendingConfigurationMissing,
  lendingConfigApi,
  type LendingConfiguration,
  type LendingConfigurationUpdateRequest,
} from '../api/services/lending-config';

export const LENDING_CONFIG_QUERY_KEY = ['lending-config'] as const;
export const LENDING_CONFIG_DEFAULTS_QUERY_KEY = ['lending-config', 'defaults'] as const;

function shouldRetry(failureCount: number, error: Error): boolean {
  if (isLendingConfigurationMissing(error) || isLendingConfigurationIncomplete(error)) return false;
  return failureCount < 2;
}

export function useLendingConfig() {
  return useQuery<LendingConfiguration, Error>({
    queryKey: LENDING_CONFIG_QUERY_KEY,
    queryFn: () => lendingConfigApi.get(),
    retry: shouldRetry,
  });
}

/** The platform defaults; disabled until the page calls refetch(). */
export function useLendingConfigDefaults() {
  return useQuery<LendingConfiguration, Error>({
    queryKey: LENDING_CONFIG_DEFAULTS_QUERY_KEY,
    queryFn: () => lendingConfigApi.getDefaults(),
    enabled: false,
  });
}

export function useUpdateLendingConfig() {
  const queryClient = useQueryClient();
  return useMutation<LendingConfiguration, Error, LendingConfigurationUpdateRequest>({
    mutationFn: (body) => lendingConfigApi.update(body),
    onSuccess: (saved) => {
      queryClient.setQueryData<LendingConfiguration>(LENDING_CONFIG_QUERY_KEY, saved);
    },
  });
}
